<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import AssistantBasicSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantBasicSettingsForm.vue';
import AssistantSystemSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantSystemSettingsForm.vue';
import AssistantSalesSkillForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantSalesSkillForm.vue';
import AssistantControlItems from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantControlItems.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const isCaptainV2Enabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAPTAIN_V2)
);
const route = useRoute();
const router = useRouter();
const store = useStore();

const deleteAssistantDialog = ref(null);
const activeTab = ref('general');

const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const assistants = useMapGetter('captainAssistants/getRecords');
const captainInboxes = useMapGetter('captainInboxes/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);

const hasConnectedInbox = computed(() => captainInboxes.value.length > 0);

const controlItems = computed(() => {
  return [
    {
      name: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.TITLE'
      ),
      description: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.DESCRIPTION'
      ),
      routeName: 'captain_assistants_guardrails_index',
    },
    {
      name: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.TITLE'
      ),
      description: t(
        'CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.DESCRIPTION'
      ),
      routeName: 'captain_assistants_guidelines_index',
    },
  ];
});

const settingsTabs = computed(() => {
  const tabs = [
    {
      key: 'GENERAL',
      value: 'general',
    },
  ];
  if (hasConnectedInbox.value) {
    tabs.push({ key: 'SALES_SKILL', value: 'sales_skill' });
  }
  if (isCaptainV2Enabled.value) {
    tabs.push({ key: 'CONTROLS', value: 'controls' });
  }
  tabs.push({ key: 'DANGER', value: 'danger' });
  return tabs;
});

const tabs = computed(() =>
  settingsTabs.value.map(tab => ({
    label: t(`CAPTAIN.ASSISTANTS.SETTINGS.TABS.${tab.key}`),
    value: tab.value,
  }))
);

const activeTabIndex = computed(() => {
  const index = settingsTabs.value.findIndex(
    tab => tab.value === activeTab.value
  );
  return index >= 0 ? index : 0;
});

const handleTabChange = tab => {
  if (tab?.value) activeTab.value = tab.value;
};

watch(
  assistantId,
  newId => {
    if (!newId) return;
    store.dispatch('captainAssistants/show', newId);
    store.dispatch('captainInboxes/get', { assistantId: newId });
    activeTab.value = 'general';
  },
  { immediate: true }
);

watch([hasConnectedInbox, isCaptainV2Enabled], () => {
  const valid = settingsTabs.value.some(tab => tab.value === activeTab.value);
  if (!valid) activeTab.value = 'general';
});

const handleSubmit = async updatedAssistant => {
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      ...updatedAssistant,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleDelete = () => {
  deleteAssistantDialog.value.dialogRef.open();
};

const handleDeleteSuccess = () => {
  const remainingAssistants = assistants.value.filter(
    a => a.id !== assistantId.value
  );

  if (remainingAssistants.length > 0) {
    const nextAssistant = remainingAssistants[0];
    router.push({
      name: 'captain_assistants_settings_index',
      params: {
        accountId: route.params.accountId,
        assistantId: nextAssistant.id,
      },
    });
  } else {
    router.push({
      name: 'captain_assistants_create_index',
      params: { accountId: route.params.accountId },
    });
  }
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          class="w-full max-w-3xl [&>button]:flex-1 bg-n-alpha-black2"
          @tab-changed="handleTabChange"
        />

        <div v-if="activeTab === 'general'" class="flex flex-col gap-6">
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.BASIC_SETTINGS.TITLE')"
              :description="
                t('CAPTAIN.ASSISTANTS.SETTINGS.BASIC_SETTINGS.DESCRIPTION')
              "
            />
            <AssistantBasicSettingsForm
              :assistant="assistant"
              @submit="handleSubmit"
            />
          </div>
          <span class="h-px w-full bg-n-weak mt-2" />
          <div class="flex flex-col gap-6">
            <SettingsHeader
              :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.TITLE')"
              :description="
                t('CAPTAIN.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.DESCRIPTION')
              "
            />
            <AssistantSystemSettingsForm
              :assistant="assistant"
              @submit="handleSubmit"
            />
          </div>
        </div>

        <div
          v-else-if="activeTab === 'sales_skill' && hasConnectedInbox"
          class="flex flex-col gap-6"
        >
          <SettingsHeader
            :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.TITLE')"
            :description="
              t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.DESCRIPTION')
            "
          />
          <AssistantSalesSkillForm
            v-if="assistant"
            :assistant="assistant"
            @submit="handleSubmit"
          />
        </div>

        <div
          v-else-if="activeTab === 'controls' && isCaptainV2Enabled"
          class="flex flex-col gap-6"
        >
          <SettingsHeader
            :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.TITLE')"
            :description="
              t('CAPTAIN.ASSISTANTS.SETTINGS.CONTROL_ITEMS.DESCRIPTION')
            "
          />
          <div class="flex flex-col gap-6 max-w-xl">
            <AssistantControlItems
              v-for="item in controlItems"
              :key="item.name"
              :control-item="item"
            />
          </div>
        </div>

        <div
          v-else-if="activeTab === 'danger'"
          class="flex items-end justify-between w-full max-w-2xl gap-4"
        >
          <div class="flex flex-col gap-2">
            <h6 class="text-n-slate-12 text-base font-medium">
              {{ t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.TITLE') }}
            </h6>
            <span class="text-n-slate-11 text-sm">
              {{ t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.DESCRIPTION') }}
            </span>
          </div>
          <div class="flex-shrink-0">
            <Policy :permissions="['administrator']">
              <Button
                :label="
                  t('CAPTAIN.ASSISTANTS.SETTINGS.DELETE.BUTTON_TEXT', {
                    assistantName: assistant.name,
                  })
                "
                color="ruby"
                class="max-w-56 !w-fit"
                @click="handleDelete"
              />
            </Policy>
          </div>
        </div>
      </div>
    </template>
    <DeleteDialog
      v-if="assistant"
      ref="deleteAssistantDialog"
      :entity="assistant"
      type="Assistants"
      translation-key="ASSISTANTS"
      @delete-success="handleDeleteSuccess"
    />
  </PageLayout>
</template>
